import { useQueryClient } from '@tanstack/react-query';

import useInfiniteQuery from '@/core/hooks/api/useInfiniteQuery';
import useQuery from '@/core/hooks/api/useQuery';
import { ErrorKind } from '@/core/lib/fetch';
import Api from '@/core/lib/new-architecture/api';
import getToken, { getTokenNoFail } from '@/core/lib/new-architecture/query/token';
import type { InfiniteQuery, Query } from '@/core/lib/new-architecture/query/types.query';

const keys = {
  adless: () => ['/user/adless'] as const,
  base: ['KEY_TO_PREVENT_COLLISIONS', '/user'] as const,
  contacts: () => [...keys.base, '/contacts'] as const,
  contactsFollow: () => [...keys.base, '/contacts', '/follows'] as const,
  contactsFollowing: () => [...keys.base, '/contacts', '/following'] as const,
  control: () => [...keys.base, '/control'] as const,
  donationsSearch: () => [...keys.base, '/searches'] as const,
  user: (uuid: string) => [...keys.base, uuid] as const,
};

const useAdless: Query<Awaited<ReturnType<typeof Api.query.user.adless>>> = ({ token, enabled, refetchInterval }) =>
  useQuery({ queryKey: keys.adless(), queryFn: async () => Api.query.user.adless(await getToken(token)), enabled, refetchInterval, refetchIntervalInBackground: true });

const useCurrent: Query<Awaited<ReturnType<typeof Api.query.user.get>>> = ({ enabled, token }) => {
  return useQuery({
    queryKey: keys.base,
    queryFn: async () => {
      return Api.query.user.get(await getToken(token));
    },
    enabled,
    retry: (failureCount, error) => {
      if (error.kind === ErrorKind.Unauthorized) {
        return false;
      }

      return failureCount < 3;
    },
  });
};

const useControl: Query<Awaited<ReturnType<typeof Api.query.user.control>>> = ({ enabled, token }) => {
  return useQuery({
    queryKey: keys.control(),
    queryFn: async () => {
      return Api.query.user.control(await getToken(token));
    },
    enabled,
    retry: (failureCount, error) => {
      if (error.kind === ErrorKind.Unauthorized) {
        return false;
      }

      return failureCount < 3;
    },
  });
};

const useContacts: Query<Awaited<ReturnType<typeof Api.query.user.contacts>>> = ({ enabled, token }) => {
  return useQuery({
    queryKey: keys.contacts(),
    queryFn: async () => {
      return Api.query.user.contacts(await getToken(token));
    },
    enabled,
    retry: (failureCount, error) => {
      if (error.kind === ErrorKind.Unauthorized) {
        return false;
      }

      return failureCount < 3;
    },
  });
};

const CONTACT_FOLLOWS_SIZE = 20;

const useContactsFollows: InfiniteQuery<Awaited<ReturnType<typeof Api.query.user.contactsFollows>>> = ({ enabled, token }) => {
  return useInfiniteQuery({
    queryKey: keys.contactsFollow(),
    queryFn: async ({ pageParam }) => Api.query.user.contactsFollows({ from: pageParam as number, size: CONTACT_FOLLOWS_SIZE }, await getToken(token)),
    enabled,
    initialData: undefined,
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const { length } = pages.flatMap(page => page.follows);
      if (length >= lastPage.total) return undefined;

      return (length / CONTACT_FOLLOWS_SIZE) * CONTACT_FOLLOWS_SIZE;
    },
  });
};

const useContactsFollowing: InfiniteQuery<Awaited<ReturnType<typeof Api.query.user.contactsFollowing>>> = ({ enabled, token }) => {
  return useInfiniteQuery({
    queryKey: keys.contactsFollowing(),
    queryFn: async ({ pageParam }) => Api.query.user.contactsFollowing({ from: pageParam as number, size: CONTACT_FOLLOWS_SIZE }, await getToken(token)),
    enabled,
    initialData: undefined,
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const { length } = pages.flatMap(page => page.follows);
      if (length >= lastPage.total) return undefined;

      return (length / CONTACT_FOLLOWS_SIZE) * CONTACT_FOLLOWS_SIZE;
    },
  });
};

interface PublicProfileParams {
  uuid?: string;
}

const usePublicProfile: Query<Awaited<ReturnType<typeof Api.query.user.user>>, PublicProfileParams> = ({ data: { uuid }, token }) => {
  return useQuery({
    queryKey: keys.user(uuid as string),
    queryFn: async () => Api.query.user.user(uuid as string, await getTokenNoFail(token)),
    enabled: !!uuid,
    retry: 0,
  });
};

usePublicProfile.prefetch = async ({ queryClient, uuid }) => {
  await queryClient.prefetchQuery({
    queryKey: keys.user(uuid as string),
    queryFn: async () => Api.query.user.user(uuid as string),
  });
};

const useDonationsUserSearches: Query<Awaited<ReturnType<typeof Api.query.donationsUserSearchApi.getDonationsUserSearch>>> = ({ token, enabled }) =>
  useQuery({
    queryKey: keys.donationsSearch(),
    queryFn: async () => Api.query.donationsUserSearchApi.getDonationsUserSearch(await getToken(token)),
    enabled,
  });

const useInvalidate = () => {
  const queryClient = useQueryClient();

  const invalidate = () => queryClient.invalidateQueries({ queryKey: keys.base });
  const invalidateControl = () => queryClient.invalidateQueries({ queryKey: keys.control() });
  const invalidateDonationsSearch = () => queryClient.invalidateQueries({ queryKey: keys.donationsSearch() });
  const invalidateContact = () => queryClient.invalidateQueries({ queryKey: keys.contacts() });

  return { invalidate, invalidateControl, invalidateDonationsSearch, invalidateContact };
};

export default { useAdless, useCurrent, useControl, useContacts, useContactsFollows, usePublicProfile, useInvalidate, useDonationsUserSearches, useContactsFollowing };
